import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Grid,
  IconButton,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import Loading from "../Layout/Loading";
import { useApi } from "../../utils/api";
import BuyProductsModal from "../ClientPortal/BuyProductsModal";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import InstagramFeed from "./InstagramFeed";

const PublicCoachProfile = () => {
  const { slug } = useParams();
  const { getCoachProfilePublic, getProducts } = useApi();
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [assignedProducts, setAssignedProducts] = useState([]);
  const stripePromise = useMemo(
    () => loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY),
    []
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const p = await getCoachProfilePublic(slug);
      setProfile(p);
      const publicIds = (p?.publicProducts || []).filter(Boolean);
      if (publicIds.length) {
        const all = (await getProducts()) || [];
        const filtered = [];
        all.forEach((prod) => {
          const prices = Array.isArray(prod.prices) ? prod.prices : [];
          const matchedPrices = prices.filter((pr) =>
            publicIds.includes(pr.priceId)
          );
          if (matchedPrices.length) {
            filtered.push({ ...prod, prices: matchedPrices });
          } else if (publicIds.includes(prod.priceId || prod.id)) {
            filtered.push(prod);
          }
        });
        setProducts(filtered);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <Container sx={{ mt: 10 }}>
        <Loading />
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container sx={{ mt: 10 }}>
        <Typography variant="h6">Coach not found</Typography>
      </Container>
    );
  }

  const galleryImages = profile.galleryImages || [];
  const coachId = profile.coachId;

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", color: "#000" }}>
      {/* Banner Section - Asymmetrical */}
      {profile.bannerImageUrl && (
        <Box
          sx={{
            position: "relative",
            height: { xs: "25vh", md: "30vh" },
            overflow: "hidden",
            mb: { xs: -8, md: -12 },
          }}
        >
          <img
            src={profile.bannerImageUrl}
            alt="Banner"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: "scale(1.1)",
              filter: "grayscale(100%)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: { xs: "10%", md: "15%" },
              right: { xs: "10%", md: "auto" },
              width: { xs: "80%", md: "40%" },
              height: { xs: 120, md: 200 },
              bgcolor: "#fff",
              transform: { md: "translateY(50%) rotate(-2deg)" },
              border: "3px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
              zIndex: 10,
            }}
          >
            {profile.imageUrl && (
              <Box
                sx={{
                  width: { xs: 100, md: 150 },
                  height: { xs: 100, md: 150 },
                  borderRadius: "50%",
                  border: "4px solid #000",
                  overflow: "hidden",
                  bgcolor: "#fff",
                }}
              >
                <img
                  src={profile.imageUrl}
                  alt={profile.displayName || profile.slug}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Container 
        maxWidth="xl"
        sx={{ 
          pb: 8, 
          px: { xs: 2, sm: 3, md: 4 },
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Profile Info Section - Asymmetrical */}
        <Box
          sx={{
            mb: { xs: 8, md: 10 },
            ml: { xs: 0, md: profile.bannerImageUrl ? "45%" : 0 },
            mt: { xs: 4, md: profile.bannerImageUrl ? 0 : 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 4 },
            alignItems: { xs: "flex-start", md: "flex-start" },
          }}
        >
          {!profile.bannerImageUrl && profile.imageUrl && (
            <Box
              sx={{
                width: { xs: 120, md: 180 },
                height: { xs: 120, md: 180 },
                borderRadius: "50%",
                border: "4px solid #000",
                overflow: "hidden",
                flexShrink: 0,
                transform: "rotate(-5deg)",
              }}
            >
              <img
                src={profile.imageUrl}
                alt={profile.displayName || profile.slug}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                mb: 2,
                transform: { md: "rotate(-1deg)" },
                textTransform: "uppercase",
                letterSpacing: 2,
                color: "#000",
              }}
            >
              {profile.displayName || profile.slug}
            </Typography>
            {profile.instagramUsername && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <InstagramIcon sx={{ color: "#E4405F" }} />
                <Typography variant="body1" sx={{ color: "#333" }}>
                  @{profile.instagramUsername.replace("@", "")}
                </Typography>
              </Box>
            )}
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "pre-wrap",
                color: "#333",
                lineHeight: 1.8,
                maxWidth: { md: "80%" },
              }}
            >
              {profile.bio || "Coach bio coming soon."}
            </Typography>
          </Box>
        </Box>

        {/* Products Section - Asymmetrical */}
        <Box sx={{ mb: { xs: 8, md: 10 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              mb: { xs: 4, md: 5 },
              textTransform: "uppercase",
              letterSpacing: 2,
              transform: "rotate(1deg)",
              color: "#000",
            }}
          >
            Products
          </Typography>
          {!products.length && (
            <Typography sx={{ color: "#666", fontStyle: "italic" }}>
              This coach has no products available right now.
            </Typography>
          )}
          <Grid container spacing={4}>
            {products.map((prod, index) => {
              const rotation = index % 2 === 0 ? -0.5 : 0.5;
              return (
                <Grid item xs={12} sm={6} md={4} key={prod.priceId || prod.id}>
                  <Box
                    sx={{
                      bgcolor: "#f5f5f5",
                      border: "2px solid #000",
                      p: 2,
                      transform: `rotate(${rotation}deg)`,
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: `rotate(${rotation}deg) scale(1.02)`,
                        bgcolor: "#e8e8e8",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        color: "#000",
                      }}
                    >
                      {prod.name || prod.id}
                    </Typography>
                    <Typography sx={{ color: "#333", mb: 2 }}>
                      {prod.description}
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}
                    >
                      {Array.isArray(prod.prices) && prod.prices.length
                        ? prod.prices.map((pr) => (
                            <Box
                              key={pr.priceId}
                              sx={{
                                bgcolor: "#000",
                                border: "1px solid #000",
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "#fff" }}
                              >
                                {pr.nickname ||
                                  pr.metadata?.description ||
                                  pr.priceId}{" "}
                                —{" "}
                                {pr.unitAmount !== undefined
                                  ? `$${(pr.unitAmount / 100).toFixed(2)}`
                                  : ""}
                              </Typography>
                            </Box>
                          ))
                        : null}
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => {
                        const mapped =
                          Array.isArray(prod.prices) && prod.prices.length
                            ? prod.prices.map((pr) => ({
                                productId: prod.productId || prod.id,
                                priceId: pr.priceId,
                                productName: prod.name || prod.id,
                                priceDescription:
                                  pr.nickname ||
                                  pr.metadata?.description ||
                                  prod.description ||
                                  "",
                                price:
                                  pr.unitAmount !== undefined
                                    ? pr.unitAmount / 100
                                    : 0,
                              }))
                            : [
                                {
                                  productId: prod.productId || prod.id,
                                  priceId: prod.priceId || prod.id,
                                  productName: prod.name || prod.id,
                                  priceDescription: prod.description || "",
                                  price: prod.price || 0,
                                },
                              ];
                        setAssignedProducts(mapped);
                        setSelectedProduct(prod);
                        setModalOpen(true);
                      }}
                      sx={{
                        bgcolor: "#000",
                        color: "#fff",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        "&:hover": {
                          bgcolor: "#333",
                        },
                      }}
                    >
                      Buy
                    </Button>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Gallery Section - Asymmetrical Masonry Grid */}
        {galleryImages.length > 0 && (
          <Box sx={{ mb: { xs: 10, md: 12 } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                mb: { xs: 4, md: 5 },
                textTransform: "uppercase",
                letterSpacing: 2,
                transform: "rotate(-1deg)",
                color: "#000",
              }}
            >
              Gallery
            </Typography>
            <Grid container spacing={3}>
              {galleryImages.map((url, index) => {
                const isLarge = index % 7 === 0 || index % 7 === 3;
                const rotation = index % 2 === 0 ? -1.5 : 1.5;
                return (
                  <Grid
                    item
                    xs={12}
                    sm={isLarge ? 8 : 4}
                    md={isLarge ? 6 : 3}
                    key={index}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        border: "2px solid #000",
                        transform: `rotate(${rotation}deg)`,
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: `rotate(${rotation}deg) scale(1.05)`,
                          zIndex: 1,
                        },
                      }}
                    >
                      <img
                        src={url.url || url}
                        alt={`Gallery ${index + 1}`}
                        style={{
                          width: "100%",
                          height: isLarge ? 280 : 180,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Instagram Feed Section */}
        {coachId && (
          <Box sx={{ mb: { xs: 10, md: 12 } }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                mb: { xs: 4, md: 5 },
                textTransform: "uppercase",
                letterSpacing: 2,
                transform: "rotate(1deg)",
                color: "#000",
              }}
            >
              Instagram
            </Typography>
            <InstagramFeed userId={coachId} />
          </Box>
        )}
      </Container>

      {selectedProduct && (
        <Elements stripe={stripePromise}>
          <BuyProductsModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            assignedProducts={assignedProducts}
            hasSavedPaymentMethod={false}
            stripeCustomerId={null}
            onSuccess={() => setModalOpen(false)}
          />
        </Elements>
      )}
    </Box>
  );
};

export default PublicCoachProfile;
